import type { OrganizationScope } from "@/types/organization-scope"
import type { CallerHandledAuthFailureOptions } from "@/utils/callerHandledAuthFailure"
import { API_GRAY_HEADER } from "@/utils/apiGrayHeader"
import { getCallerHandledAuthFailureConfig } from "@/utils/callerHandledAuthFailure"
import { request } from "@/utils/service"

export interface MyOrganizationsResponse {
  organizations: OrganizationScope[]
  /** 当前 access token 已确认的运行态 scope；旧后端可能不返回。 */
  currentScope?: ScopeSwitchCurrentScope
}

export type MyOrganizationsApiResponse = IApiResponseData<MyOrganizationsResponse>

export interface ScopeSwitchRequest {
  scopeId: string
  scopeType?: "SHOP" | "CHAIN_STORE" | "CHAIN_REGION" | "CHAIN_HEADQUARTER"
  brandId?: number
  regionId?: number
  shopId?: number
  roleId?: number
}

export interface ScopeSwitchCurrentScope {
  scopeId: string
  scopeType: "SHOP" | "CHAIN_STORE" | "CHAIN_REGION" | "CHAIN_HEADQUARTER"
  brandId?: number
  regionId?: number
  shopId?: number
  merchantUserId?: number
  merchantUserIds?: number[]
  roleId?: number
  roleIds?: number[]
  accessibleShopIds?: number[]
}

export interface ScopeSwitchTicket {
  authCode: string
  ticket: string
  expiresIn: number
  /** 新版服务端返回；旧版生产票据只包含 authCode/ticket/expiresIn。 */
  currentScope?: ScopeSwitchCurrentScope
}

export const HEADQUARTERS_SCOPE_ROLE_UNAVAILABLE_CODE = 47211

export class OrganizationScopeSwitchError extends Error {
  businessCode: number

  constructor(businessCode: number, message: string) {
    super(message)
    this.name = "OrganizationScopeSwitchError"
    this.businessCode = businessCode
  }
}

export function isHeadquartersScopeRoleUnavailableError(error: unknown) {
  return error instanceof OrganizationScopeSwitchError
    && error.businessCode === HEADQUARTERS_SCOPE_ROLE_UNAVAILABLE_CODE
}

export function getMyOrganizations(options: CallerHandledAuthFailureOptions = {}) {
  return request<MyOrganizationsApiResponse>({
    url: "teashop_merchant/me/organizations",
    method: "GET",
    businessContextSystemVersion: null,
    ...getCallerHandledAuthFailureConfig(options),
    customHeaders: {
      [API_GRAY_HEADER]: "true",
    },
  })
}

export function switchOrganizationScope(
  data: ScopeSwitchRequest,
  options: CallerHandledAuthFailureOptions = {},
) {
  return request<IApiResponseData<ScopeSwitchTicket>>({
    url: "teashop_merchant/me/scope/switch",
    method: "POST",
    data,
    businessContextSystemVersion: null,
    skipBusinessCodeCheck: true,
    ...getCallerHandledAuthFailureConfig(options),
    customHeaders: {
      [API_GRAY_HEADER]: "true",
    },
  }).then((response) => {
    if (Number(response.code) === 0) {
      return response
    }
    throw new OrganizationScopeSwitchError(
      Number(response.code),
      response.msg || "机构切换失败",
    )
  })
}
